(function () {
  'use strict';

  console.log('[aegis-cart-drawer] script loaded');

  const POLL_INTERVAL_MS = 300;

  /**
   * Recursively searches for `selector`, descending into any shadow roots
   * encountered. Recharge's cart drawer renders inside a Web Component's
   * shadow root (confirmed by the `part="money"` / `part="money discount"`
   * attributes in its markup, which is the CSS Shadow Parts spec — only
   * meaningful inside a shadow tree). Plain document.querySelector and a
   * document-level MutationObserver cannot see across that boundary, so we
   * have to walk it manually.
   */
  function deepFind(selector, root) {
    root = root || document;

    const direct = root.querySelector(selector);
    if (direct) return direct;

    const all = root.querySelectorAll('*');
    for (let i = 0; i < all.length; i++) {
      const el = all[i];
      if (el.shadowRoot) {
        const found = deepFind(selector, el.shadowRoot);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Strips currency symbols/commas and returns a numeric value.
   */
  function parseMoney(text) {
    if (!text) return null;
    const match = text.replace(/,/g, '').match(/[\d.]+/);
    return match ? parseFloat(match[0]) : null;
  }

  /**
   * Re-applies whatever currency symbol/prefix was in the sample text
   * (e.g. "$") to a freshly computed numeric value.
   */
  function formatMoney(value, sampleText) {
    const symbolMatch = sampleText ? sampleText.match(/^[^\d]*/) : null;
    const symbol = symbolMatch && symbolMatch[0] ? symbolMatch[0] : '$';
    return symbol + value.toFixed(2);
  }

  /**
   * Per-item committed price state, keyed by a STABLE identity string
   * (title + variant/plan text) rather than the DOM node itself — Recharge
   * fully tears down and recreates each line item's markup on quantity
   * change (confirmed: a freshly-created node can carry a stale price
   * value that hasn't caught up to the new quantity yet), so a node-keyed
   * WeakMap or per-node MutationObserver can't reliably track "the same
   * logical item" across that rebuild. Keying by content instead lets us
   * correctly recognize continuity and only hide/reveal based on whether
   * the reading has actually stabilized.
   */
  const itemPriceState = new Map();

  function getPlanLabel(dl) {
    const planNameEl = Array.from(dl.querySelectorAll('p')).filter(
      (p) => !p.classList.contains('aegis-subscribe-save-line') && !p.classList.contains('aegis-founding-badge')
    )[1];
    const planName = planNameEl ? planNameEl.textContent.trim() : 'Subscription';
    return planName.replace(/subscription/i, '').trim() || 'Founding Member';
  }

  /**
   * Builds a stable key from the item's title + non-injected <dl> text,
   * so the same logical cart line is recognized as "the same item" even
   * after Recharge recreates its DOM node on a quantity change.
   */
  function getItemStableKey(itemEl) {
    const dl = itemEl.querySelector('dl');
    if (!dl) return null;

    const titleEl = itemEl.querySelector('.font-semibold');
    const title = titleEl ? titleEl.textContent.trim() : '';

    const variantParts = Array.from(dl.querySelectorAll('p'))
      .filter((p) => !p.classList.contains('aegis-subscribe-save-line') && !p.classList.contains('aegis-founding-badge'))
      .map((p) => p.textContent.trim());

    const key = title + '::' + variantParts.join('|');
    console.log('[aegis-price-debug] getItemStableKey ->', key, '(titleEl matched:', titleEl, ')');
    return key;
  }

  /**
   * Renders (or hides) the founding-member badge for one line item.
   * The ENTIRE line is hidden — not just the price — whenever the current
   * (price, quantity) reading hasn't yet been confirmed identical on two
   * consecutive poll cycles. This means a transitional mismatch (e.g. a
   * freshly-recreated node whose price hasn't caught up to a just-changed
   * quantity) never renders any number, correct or not — it simply hides
   * the line for one ~300ms poll tick until the reading settles.
   */
  function decorateSubscriptionItem(itemEl) {
    const dl = itemEl.querySelector('dl');
    if (!dl) return;

    let subscribeLine = dl.querySelector('.aegis-subscribe-save-line');
    if (!subscribeLine) {
      subscribeLine = document.createElement('p');
      subscribeLine.className = 'aegis-subscribe-save-line';
      subscribeLine.textContent = 'Subscribe & Save · Delivers Monthly';
      dl.appendChild(subscribeLine);
    }

    let badge = dl.querySelector('.aegis-founding-badge');

    const priceEl = itemEl.querySelector('[part="money discount"]') || itemEl.querySelector('[part="money"]');
    const qtyInput = itemEl.querySelector('input[name="quantity"]');
    const stableKey = getItemStableKey(itemEl);

    if (!priceEl || !qtyInput || !stableKey) {
      if (badge) badge.remove();
      return;
    }

    const totalText = priceEl.textContent.trim();
    const qty = parseInt(qtyInput.value, 10) || 1;
    const totalValue = parseMoney(totalText);

    if (totalValue == null || qty <= 0) {
      if (badge) badge.remove();
      return;
    }

    const candidateKey = totalText + '|' + qty;
    const candidateFormatted = formatMoney(totalValue / qty, totalText);

    let state = itemPriceState.get(stableKey);
    if (!state) {
      state = {
        committedKey: null,
        committedValue: null,
        pendingKey: null,
        pendingSince: 0
      };
      itemPriceState.set(stableKey, state);
    }

    let action = 'NOOP (unchanged)';

    const now = Date.now();

    if (candidateKey !== state.committedKey) {

      if (candidateKey !== state.pendingKey) {
        state.pendingKey = candidateKey;
        state.pendingSince = now;
        action = 'PENDING (new)';
      } else if (now - state.pendingSince >= 700) {
        state.committedKey = candidateKey;
        state.committedValue = candidateFormatted;
        state.pendingKey = null;
        action = 'COMMIT';
      } else {
        action = 'PENDING (waiting)';
      }

    }

    console.log(
      '[aegis-price-debug]',
      new Date().toISOString().slice(11, 23),
      '| key=' + stableKey,
      '| totalText=' + totalText,
      '| qty=' + qty,
      '| candidateKey=' + candidateKey,
      '| candidateFormatted=' + candidateFormatted,
      '| action=' + action,
      '| committedKey=' + state.committedKey,
      '| committedValue=' + state.committedValue,
      '| pendingKey=' + state.pendingKey
    );

    // Hide while a new value is pending so the previous value never flashes.
    if (state.pendingKey && state.pendingKey !== state.committedKey) {
        if (badge) {
            badge.remove();
        }
    } else if (state.committedValue) {
        if (!badge) {
            badge = document.createElement('p');
            badge.className = 'aegis-founding-badge';
            dl.appendChild(badge);
        }

        badge.innerHTML =
            '★ ' + getPlanLabel(dl) + ' · ' +
            state.committedValue + '/mo locked in';
    } else if (badge) {
        badge.remove();
    }
  }

  function isSubscriptionItem(itemEl) {
    const dl = itemEl.querySelector('dl');
    if (!dl) return false;
    const originalParagraphs = Array.from(dl.querySelectorAll('p')).filter(
      (p) => !p.classList.contains('aegis-subscribe-save-line') && !p.classList.contains('aegis-founding-badge')
    );
    return originalParagraphs.length >= 2;
  }

  function injectStyles() {
    if (document.getElementById('aegis-cart-drawer-styles')) return;

    const style = document.createElement('style');
    style.id = 'aegis-cart-drawer-styles';
    style.textContent = `
      #cart-drawer{
        --storefront-drawer-button-radius: 8px;
      }

      #CartSheet{
        max-width: 520px !important;
      }
      
      .aegis-free-shipping-banner{
        border-bottom: 1px solid #dedcd7;
      }

      h1,h2,h3,h4,h5,h6{
        text-transform: uppercase;
      }
      
      .line-through{
        color: rgba(107, 107, 100, 1);
      }
      
      [part="money"].line-through{
        font-size: 14px;
      }

      footer .px-drawer .flex,
      footer .aegis-total-row .flex {
        align-items: center;
      }

      .aegis-free-shipping-banner {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        font-size: 13px;
        font-weight: 600;
        background: #eaf5d0;
        color: #3a4d1a;
      }
      .aegis-free-shipping-banner svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
      }
      .aegis-subscribe-save-line {
        font-size: 13px;
        margin: 0;
      }
      .aegis-founding-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        font-weight: 700;
        margin: 0;
      }
      .aegis-welcome-gift-notice {
        display: flex;
        gap: 10px;
        align-items: flex-start;
        margin: 12px 16px;
        padding: 12px;
        border: 1px dashed currentColor;
        border-radius: 8px;
        font-size: 13px;
      }
      .aegis-welcome-gift-notice svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        margin-top: 2px;
      }
      .aegis-trust-badges {
        display: flex;
        justify-content: center;
        gap: 16px;
        padding: 8px 16px 4px;
        font-size: 12px;
        opacity: 0.85;
      }
      .aegis-trust-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .aegis-trust-badge svg {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
      }
      .aegis-continue-shopping {
        display: block;
        text-align: center;
        padding: 4px 16px 14px;
        font-size: 12px;
        text-decoration: underline;
        cursor: pointer;
        color: inherit;
      }
      .aegis-shipping-free {
        color: #4a7a1e;
      }
      .aegis-total-row {
        font-size: 1.05em;
      }
      .aegis-billing-note {
        font-size: 12px;
        opacity: 0.7;
        margin: 0;
        padding-top: 4px;
        padding-bottom: 8px;
      }
    `;
    // Shadow DOM does NOT inherit <head> stylesheets, so this only works
    // if the drawer's root turns out to be light DOM after all. If the
    // debug snippet confirms shadow DOM, this <style> needs to be injected
    // directly into that shadow root instead — see injectStylesInto() below.
    document.head.appendChild(style);
  }

  /**
   * Injects the same stylesheet directly into a given root (document or a
   * shadow root), since shadow roots don't inherit page-level <head> styles.
   */
  function injectStylesInto(root) {
    if (root.getElementById && root.getElementById('aegis-cart-drawer-styles')) return;
    if (!root.getElementById && root.querySelector('#aegis-cart-drawer-styles')) return;

    const style = document.createElement('style');
    style.id = 'aegis-cart-drawer-styles';
    style.textContent = document.getElementById('aegis-cart-drawer-styles')?.textContent || '';
    root.appendChild(style);
  }

  function buildFreeShippingBanner() {
    const el = document.createElement('div');
    el.className = 'aegis-free-shipping-banner';
    el.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 17h4V5H2v12h3"/><path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>' +
      '<span>Free Shipping Unlocked</span>';
    return el;
  }

  function buildWelcomeGiftNotice() {
    const el = document.createElement('div');
    el.className = 'aegis-welcome-gift-notice';
    el.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>' +
      '<span><strong>Your first order</strong> includes the glass shaker bottle &amp; Field Manual — free welcome gifts.</span>';
    return el;
  }

  // TODO: confirm this is the correct handle for the AEGIS Welcome Kit PDP.
  const CONTINUE_SHOPPING_URL = '/products/aegis-welcome-kit';

  function buildTrustBadges() {
    const el = document.createElement('div');
    el.className = 'aegis-trust-badges';
    el.innerHTML =
      '<span class="aegis-trust-badge">' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5z"/></svg>' +
      '<span>45-Day Guarantee</span>' +
      '</span>' +
      '<span class="aegis-trust-badge">' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>' +
      '<span>Secure Checkout</span>' +
      '</span>';
    return el;
  }

  function buildContinueShoppingLink() {
    const a = document.createElement('a');
    a.className = 'aegis-continue-shopping';
    a.href = CONTINUE_SHOPPING_URL;
    a.textContent = 'Continue shopping';
    return a;
  }

  /**
   * The empty-cart state renders its own native "Continue shopping" button
   * (separate from the one we inject into the footer for a populated cart).
   * Found generically by text content since we don't control its markup —
   * excludes our own .aegis-continue-shopping link, and is guarded via a
   * data attribute so we don't double-bind a click listener on repeated
   * poll cycles.
   */
  function wireEmptyCartContinueShopping(drawer) {
    const candidates = drawer.querySelectorAll('a, button');
    candidates.forEach((el) => {
      if (el.classList.contains('aegis-continue-shopping')) return;
      if (el.dataset.aegisWired === 'true') return;
      if (!/^continue shopping$/i.test(el.textContent.trim())) return;

      el.dataset.aegisWired = 'true';

      if (el.tagName === 'A') {
        el.href = CONTINUE_SHOPPING_URL;
      } else {
        el.addEventListener('click', () => {
          window.location.href = CONTINUE_SHOPPING_URL;
        });
      }
    });
  }

  /**
   * "Shipping: FREE" row — reuses the same layout classes as the existing
   * Subtotal row (flex justify-between gap-2 px-drawer) so it inherits
   * Recharge's own spacing/typography from the shared shadow-root stylesheet
   * instead of us guessing pixel values.
   */
  function buildShippingRow() {
    const el = document.createElement('div');
    el.className = 'flex justify-between gap-2 px-drawer aegis-shipping-row';
    el.innerHTML = '<span>Shipping</span><span class="aegis-shipping-free font-semibold">FREE</span>';
    return el;
  }

  /**
   * "Total" row — clones the price markup (original + discounted spans,
   * including the sr-only labels and line-through styling) straight from
   * the Subtotal row, so it's always numerically identical and never goes
   * stale, without us re-deriving/formatting the price ourselves.
   */
  function buildOrUpdateTotalRow(footer, subtotalRow) {
    let row = footer.querySelector('.aegis-total-row');
    const priceDiv = subtotalRow.querySelector(':scope > div');

    if (!row) {
      row = document.createElement('div');
      row.className = 'flex justify-between gap-2 px-drawer aegis-total-row';
      const label = document.createElement('strong');
      label.textContent = 'Total';
      row.appendChild(label);
    }

    // Refresh the price content every cycle so quantity/discount changes
    // (which update the Subtotal row) propagate here too.
    const existingPriceDiv = row.querySelector(':scope > div');
    if (existingPriceDiv) existingPriceDiv.remove();
    if (priceDiv) row.appendChild(priceDiv.cloneNode(true));

    return row;
  }

  function buildBillingNote() {
    const p = document.createElement('p');
    p.className = 'px-drawer aegis-billing-note';
    p.textContent = 'Billed monthly at your locked in founding rate. Cancel anytime.';
    return p;
  }

  /**
   * Strips "(N items)" from Recharge's native Subtotal label text. Runs
   * every poll cycle (idempotent — re-running on already-stripped text is
   * a no-op) since Recharge may re-render this row on quantity changes.
   */
  function stripItemCountFromSubtotalLabel(subtotalRow) {
    const labelEl = subtotalRow.querySelector('strong') || subtotalRow.firstElementChild;
    if (!labelEl) return;
    const stripped = labelEl.textContent.replace(/\s*\(\d+\s*items?\)/i, '');
    if (labelEl.textContent !== stripped) {
      labelEl.textContent = stripped;
    }
  }



  function updateDrawer(drawer) {
    wireEmptyCartContinueShopping(drawer);

    const items = drawer.querySelectorAll('[role="listitem"]');
    let hasSubscription = false;

    items.forEach((item) => {
      if (isSubscriptionItem(item)) {
        hasSubscription = true;
        decorateSubscriptionItem(item);
      }
    });

    const header = drawer.querySelector('header');
    const listSection = drawer.querySelector('section[role="list"]');
    const existingBanner = drawer.querySelector('.aegis-free-shipping-banner');
    const existingNotice = drawer.querySelector('.aegis-welcome-gift-notice');

    if (hasSubscription) {
      if (header && !existingBanner) {
        header.insertAdjacentElement('afterend', buildFreeShippingBanner());
      }
      if (listSection && !existingNotice) {
        listSection.insertAdjacentElement('afterend', buildWelcomeGiftNotice());
      }
    } else {
      if (existingBanner) existingBanner.remove();
      if (existingNotice) existingNotice.remove();
    }

    // Trust badges + continue-shopping link are cart-wide (not tied to
    // subscription presence), inserted once into the drawer's <footer>.
    const footer = drawer.querySelector('footer');
    if (footer) {
      // Identify the existing "Subtotal (N items)" row by content rather
      // than position, in case Recharge changes markup order.
      const footerRows = Array.from(footer.children).filter((el) => el.tagName === 'DIV');
      const subtotalRow = footerRows.find((div) => /subtotal/i.test(div.textContent)) || footerRows[0];

      if (subtotalRow) {
        stripItemCountFromSubtotalLabel(subtotalRow);

        let shippingRow = footer.querySelector('.aegis-shipping-row');
        let totalRow = footer.querySelector('.aegis-total-row');
        let billingNote = footer.querySelector('.aegis-billing-note');

        if (hasSubscription) {
            if (!shippingRow) {
                shippingRow = buildShippingRow();
            }

            totalRow = buildOrUpdateTotalRow(footer, subtotalRow);

            // Insert in the correct order every time
            subtotalRow.insertAdjacentElement('afterend', shippingRow);
            shippingRow.insertAdjacentElement('afterend', totalRow);

            if (!billingNote) {
                billingNote = buildBillingNote();
            }

            totalRow.insertAdjacentElement('afterend', billingNote);
        } else {
          if (shippingRow) shippingRow.remove();
          if (totalRow) totalRow.remove();
          if (billingNote) billingNote.remove();
        }
      }

      if (!footer.querySelector('.aegis-trust-badges')) {
        footer.appendChild(buildTrustBadges());
      }
      if (!footer.querySelector('.aegis-continue-shopping')) {
        footer.appendChild(buildContinueShoppingLink());
      }
    }
  }

  function init() {
    injectStyles();

    let currentDrawer = null;

    function poll() {
      const drawer = deepFind('#cart-drawer');

      if (drawer && drawer !== currentDrawer) {
        currentDrawer = drawer;
        console.log('[aegis-cart-drawer] drawer found', drawer, 'inside root:', drawer.getRootNode());

        // Shadow roots don't inherit <head> styles — inject directly there too.
        const rootNode = drawer.getRootNode();
        if (rootNode instanceof ShadowRoot) {
          injectStylesInto(rootNode);
        }

        updateDrawer(drawer);
      } else if (drawer && drawer === currentDrawer) {
        // Same drawer instance still present — re-run to catch line-item
        // rebuilds from qty/remove changes (no MutationObserver possible
        // across a shadow boundary from outside, so polling covers it).
        updateDrawer(drawer);
      } else if (!drawer && currentDrawer) {
        console.log('[aegis-cart-drawer] drawer removed from DOM');
        currentDrawer = null;
      }
    }

    setInterval(poll, POLL_INTERVAL_MS);
    poll();
  }

  init();
})();